import { defineComponent as _defineComponent } from 'vue'
import { ref } from 'vue';
import BaseButtonSubmit from '@/views/base/BaseButtonSubmit.vue';
import BaseFormCard from '@/views/base/BaseFormCard.vue';

import sharedMessages from '@/lib/shared-messages';
import clerk, { getMessageKeyByClerkError } from '@/lib/clerk';
import { apiUrl } from '@/env';
import { defineMessages } from '@/lib/i18n';
import { i18n } from '@/plugins/vue-i18n';
import router from '@/router';


export default /*#__PURE__*/_defineComponent({
  __name: 'SamlLoginPage',
  setup(__props) {

const $t = i18n.tc.bind(i18n);

// The complete page reads `redirect` back from this URL. The value is checked
// there against the allowlist, not here.
const samlCompleteUrl = () => {
  const { redirect } = router.currentRoute.query;
  return typeof redirect === 'string' && redirect
    ? `/#/saml/complete?redirect=${encodeURIComponent(redirect)}`
    : '/#/saml/complete';
};

const m = defineMessages({
  emailRequired: 'メールアドレスを入力してください',
  emailInvalid: '有効なメールアドレスを入力してください',
  jitDisabled:
    'このメールアドレスではログインできません。管理者にユーザーアカウントの作成を依頼してください。',
  noSamlConnection:
    'あなたの所属企業ではSAML認証が設定されていません。パスワードログインをご利用いただくか、管理者にお問い合わせください。',
});

const isValidForm = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const email = ref<string>('');
const errorMessage = ref<string>('');

const updateIsValidForm = (value: boolean) => {
  isValidForm.value = value;
};

// Email validation rules
const emailRules = [
  (v: string) => !!v || $t(m.emailRequired),
  (v: string) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(v) || $t(m.emailInvalid);
  },
];

/**
 * Prepares a user for SAML login by ensuring they are a member of their
 * company's Clerk organization.
 *
 * This is necessary for existing password-authenticated users who were created
 * before SAML was configured for their company. The backend will:
 * 1. Find the user's SAML connection based on their email domain
 * 2. Add the user to the appropriate Clerk organization
 *
 * @param email - User's email address
 * @returns Organization ID if successful, null otherwise
 * @throws Error if JIT provisioning is disabled and user doesn't exist
 */
const prepareSamlLogin = async (email: string): Promise<string | null> => {
  try {
    const response = await fetch(`${apiUrl}/api/v1/prepare-saml-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (response.ok) {
      const data = await response.json();
      return data.organization_id;
    }

    if (response.status === 404) {
      // User doesn't exist - this is OK for JIT-provisioned users
      return null;
    }

    if (response.status === 400) {
      // Check for SAML configuration errors
      const data = await response.json();
      if (
        data.error === 'no_saml_connection_for_company' ||
        data.error === 'no_saml_connection_for_domain'
      ) {
        throw new Error('NO_SAML_CONNECTION');
      }
      // Other 400 errors - don't block the login attempt
      return null;
    }

    if (response.status === 403) {
      // JIT provisioning is disabled - user must be pre-created
      throw new Error('JIT_DISABLED');
    }

    // Log other errors but don't fail the login attempt
    return null;
  } catch (error) {
    // Re-throw expected errors
    if (
      error instanceof Error &&
      (error.message === 'JIT_DISABLED' ||
        error.message === 'NO_SAML_CONNECTION')
    ) {
      throw error;
    }
    return null;
  }
};

/**
 * Initiates the SAML authentication flow.
 *
 * IMPORTANT: This implementation uses a two-step process required for
 * Organization-specific SAML connections in Clerk:
 *
 * 1. Create a SignIn with the user's email identifier
 *    - This allows Clerk to determine which organization the user belongs to
 *    - Clerk uses the email to look up organization memberships
 *    - Clerk finds the SAML connection configured for that organization
 *
 * 2. Call authenticateWithRedirect on the SignIn object
 *    - This initiates the SAML SSO flow with the correct IdP
 *    - User is redirected to their company's Identity Provider (e.g., Azure AD)
 *
 * This approach is necessary because we cannot set an active organization
 * before login (no session exists yet), but we need Clerk to know which
 * organization's SAML connection to use.
 */
const submit = async () => {
  if (!clerk.loaded || !clerk.client || !isValidForm.value) {
    return;
  }

  isLoading.value = true;
  errorMessage.value = '';

  try {
    // Step 1: Prepare the user for SAML login
    // This ensures existing users are members of their organization
    // and checks if JIT provisioning is enabled for new users
    await prepareSamlLogin(email.value);

    // Step 2: Create a SignIn with the user's email
    // This is CRITICAL for Organization-specific SAML connections
    // Clerk uses this to determine which organization and SAML connection to use
    const signIn = await clerk.client.signIn.create({
      identifier: email.value,
    });

    // Step 3: Initiate SAML authentication with the created SignIn
    // This redirects the user to their company's Identity Provider (IdP)
    await signIn.authenticateWithRedirect({
      strategy: 'enterprise_sso',
      redirectUrl: '/#/saml/callback',
      // Clerk returns here after the IdP, so `redirect` rides along on this
      // URL instead of being lost on the way out.
      redirectUrlComplete: samlCompleteUrl(),
    });
  } catch (error) {
    // Check for specific error types
    if (error instanceof Error) {
      if (error.message === 'JIT_DISABLED') {
        errorMessage.value = $t(m.jitDisabled);
      } else if (error.message === 'NO_SAML_CONNECTION') {
        errorMessage.value = $t(m.noSamlConnection);
      } else {
        errorMessage.value = getMessageKeyByClerkError(error);
      }
    } else {
      errorMessage.value = getMessageKeyByClerkError(error as Error);
    }

    isLoading.value = false;
  }
};

return { __sfc: true,$t, samlCompleteUrl, m, isValidForm, isLoading, email, errorMessage, updateIsValidForm, emailRules, prepareSamlLogin, submit, BaseButtonSubmit, BaseFormCard, sharedMessages }
}

})